// Helpers de precios/facturación consistentes en toda la app.
//
// SEMÁNTICA (2026-08-11, decisión final — sustituye a la anterior
// dependiente del flag `IncludeIva`):
//   - `Plan.Price` y `AddOn.Price` viven en BD SIEMPRE como base
//     imponible (sin IVA). No hay excepciones — cualquier plan/addon
//     con importe "final ya con IVA" debe corregirse en BD dividiendo
//     por 1.21 antes de re-sincronizar con Stripe.
//   - Front y backend SIEMPRE aplican × 1.21 al enviar/mostrar el
//     precio final. El flag `includeIva` de la entidad se ignora en el
//     cálculo (queda solo por retro-compatibilidad de datos).
//   - Backend envía a Stripe `UnitAmount = price × 1.21 × 100` con
//     `TaxBehavior:"inclusive"` — Stripe cobra ese importe total y
//     desglosa el IVA hacia atrás en la factura.
//
// Fix 2026-08-11: antes esto dependía del flag `IncludeIva` y planes
// con el flag mal seteado cobraban SIN IVA aunque el front mostrara CON
// IVA — el user pagaba menos de lo esperado.

/**
 * Devuelve el precio final CON IVA (base × 1.21).
 * @param {{price: number} | null | undefined} item
 * @returns {number} precio final en €.
 */
export function resolveFinalPrice(item) {
  if (!item) return 0;
  const price = Number(item.price || 0);
  return Math.round(price * 1.21 * 100) / 100;
}

/**
 * Suma el precio final de una lista de addons (aplica IVA por elemento
 * según su flag). Útil para el total de extras del user.
 * @param {Array<{price: number, includeIva?: boolean, quantity?: number}>} items
 * @returns {number}
 */
export function sumAddOnsFinal(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  return items.reduce((sum, it) => {
    const qty = it.quantity || 1;
    return sum + resolveFinalPrice(it) * qty;
  }, 0);
}

/**
 * Formatea un importe monetario con 2 decimales y símbolo €.
 * @param {number} amount
 */
export function formatEur(amount) {
  return `${(Number(amount) || 0).toFixed(2)}€`;
}

// Tipo de IVA en España — cuando se necesite adaptar por país habrá que
// leer un flag del user/plan (fuera del scope actual).
export const IVA_RATE = 0.21;

/**
 * Devuelve el precio BASE (sin IVA) del item. Idéntico al `item.price`
 * porque BD ya guarda la base imponible — dejamos el getter por
 * consistencia con `resolveFinalPrice` y para poder cambiar la fuente
 * en el futuro sin tocar los callers.
 */
export function resolveBasePrice(item) {
  if (!item) return 0;
  return Number(item.price || 0);
}

/**
 * Devuelve el importe del IVA de este item. Siempre 21% de la base.
 */
export function resolveIvaAmount(item) {
  if (!item) return 0;
  const base = resolveBasePrice(item);
  return Math.round(base * IVA_RATE * 100) / 100;
}

/**
 * Desglose completo del precio de un item (plan o addon).
 * Devuelve `{ base, iva, total }` con 2 decimales.
 */
export function priceBreakdown(item) {
  const base = resolveBasePrice(item);
  const iva = Math.round(base * IVA_RATE * 100) / 100;
  const total = Math.round((base + iva) * 100) / 100;
  return { base, iva, total };
}

/**
 * Genera un caption humano tipo `"Base 20,00€ + IVA 4,20€"` para
 * mostrar bajo el precio total en el UI.
 */
export function formatIvaCaption(item) {
  const { base, iva } = priceBreakdown(item);
  if (iva === 0) return "";
  return `Base ${base.toFixed(2)}€ + IVA ${iva.toFixed(2)}€`;
}
